import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Users,
  Send,
  MessageSquare,
  X,
  Volume2,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import { SamenMeditation, ContentConfig } from '@/types/custom';
import { fixDropboxUrl, normalizeImageUrl } from '@/lib/utils';
import { BreathingOverlay } from '@/components/breathing-overlay';
import { BreathingPhase } from '@/lib/hooks/use-exercises';

interface Participant {
  id: string;
  naam: string;
  avatar_url: string | null;
}

interface Message {
  id: string;
  user_id: string;
  samen_id: string;
  content: string;
  created_at: string;
  user_name?: string;
  avatar_url?: string;
}

interface SamenMeditationSessionProps {
  meditation: SamenMeditation;
  samenId: string;
  onEnded?: () => void;
}

export function SamenMeditationSession({
  meditation,
  samenId,
  onEnded,
}: SamenMeditationSessionProps) {
  const { profile } = useProfile();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [timeLeft, setTimeLeft] = useState('');
  const [progress, setProgress] = useState(0);
  const [ended, setEnded] = useState(false);
  const [showChat, setShowChat] = useState(false);

  // Audio state
  const voiceAudioRef = useRef<HTMLAudioElement>(null);
  const musicAudioRef = useRef<HTMLAudioElement>(null);
  const [voiceConfigs, setVoiceConfigs] = useState<ContentConfig[]>([]);
  const [musicConfig, setMusicConfig] = useState<ContentConfig | null>(null);
  const [selectedVoiceIdx, setSelectedVoiceIdx] = useState(0);
  const isIOS = (window as any).Capacitor?.getPlatform?.() === 'ios';
  const [volume, setVolume] = useState(0.7);
  const [audioReady, setAudioReady] = useState(false);

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const startTime = new Date(meditation.scheduled_at);
  const durationMs = (meditation.duration_minutes ?? 30) * 60 * 1000;
  const endTime = new Date(startTime.getTime() + durationMs);
  const [preCountdown, setPreCountdown] = useState<number | null>(null);
  const [contentImageUrl, setContentImageUrl] = useState<string | null>(null);
  const imageUrl = normalizeImageUrl(contentImageUrl || meditation.image_url) || contentImageUrl || meditation.image_url;

  // Exercise/breathing state
  const [exerciseData, setExerciseData] = useState<{
    phases: BreathingPhase[];
    bpm: number;
    introDelay: number;
    totalRounds: number;
  } | null>(null);
  const [audioTime, setAudioTime] = useState(0);
  const isExercise = meditation.activity_type === 'exercise';
  const isBreathing = !!exerciseData;

  const selectedVoiceConfig = voiceConfigs[selectedVoiceIdx] ?? null;
  const voiceUrl = selectedVoiceConfig
    ? fixDropboxUrl(selectedVoiceConfig.audio_link) || selectedVoiceConfig.audio_link
    : null;
  const musicUrl = musicConfig
    ? fixDropboxUrl(musicConfig.audio_link) || musicConfig.audio_link
    : null;

  // Fetch content configs by matching meditation title
  useEffect(() => {
    const fetchConfigs = async () => {
      const { data } = await supabase
        .from('content')
        .select('id, title, image_url, content_configs(*)')
        .ilike('title', `%${meditation.meditation_title}%`)
        .limit(1)
        .single();

      if (data) {
        if ((data as any).image_url) {
          setContentImageUrl((data as any).image_url);
        }
        const configs = (data as any).content_configs as ContentConfig[];
        if (configs) {
          const voices = configs
            .filter((c) => c.voice)
            .sort((a, b) => (a.voice_name ?? '').localeCompare(b.voice_name ?? ''));
          const music = configs.find((c) => !c.voice) ?? null;
          setVoiceConfigs(voices);
          setMusicConfig(music);
          setAudioReady(true);
        }
      }
    };

    fetchConfigs();
  }, [meditation.meditation_title]);

  // Fetch exercise data if this is an exercise activity
  useEffect(() => {
    if (!isExercise) return;

    const fetchExercise = async () => {
      const { data } = await supabase
        .from('exercises')
        .select('exercise_type, phases, bpm, intro_duration, rounds')
        .ilike('title', `%${meditation.meditation_title}%`)
        .limit(1)
        .single();

      if (data && data.exercise_type === 'breathing' && data.phases) {
        setExerciseData({
          phases: data.phases as unknown as BreathingPhase[],
          bpm: data.bpm ?? 80,
          introDelay: data.intro_duration ?? 75,
          totalRounds: data.rounds ?? 9,
        });
      }
    };

    fetchExercise();
  }, [meditation.meditation_title, isExercise]);

  // Track audio time for breathing overlay
  useEffect(() => {
    if (!isBreathing) return;
    const el = voiceAudioRef.current;
    if (!el) return;

    const update = () => setAudioTime(el.currentTime);
    el.addEventListener('timeupdate', update);
    return () => el.removeEventListener('timeupdate', update);
  }, [isBreathing, voiceConfigs]);

  // Helper: seek an audio element to the session's elapsed position and play
  const seekToSessionPosition = (el: HTMLAudioElement) => {
    const elapsedSec = Math.max(0, (Date.now() - startTime.getTime()) / 1000);
    const dur = el.duration;
    if (dur && isFinite(dur) && dur > 0) {
      el.currentTime = elapsedSec % dur;
    }
    el.volume = volume;
    el.play().catch((err) => console.log('Autoplay prevented:', err));
  };

  // Setup music audio
  useEffect(() => {
    if (!audioReady || !musicUrl) return;
    const el = musicAudioRef.current;
    if (!el) return;

    const handler = () => {
      if (preCountdown === null) seekToSessionPosition(el);
      el.removeEventListener('loadedmetadata', handler);
    };

    el.addEventListener('loadedmetadata', handler);
    el.load();

    return () => el.removeEventListener('loadedmetadata', handler);
  }, [audioReady, musicUrl]);

  // Setup voice audio
  useEffect(() => {
    if (!audioReady || !voiceUrl) return;
    const el = voiceAudioRef.current;
    if (!el) return;

    const handler = () => {
      if (preCountdown === null) seekToSessionPosition(el);
      el.removeEventListener('loadedmetadata', handler);
    };

    el.addEventListener('loadedmetadata', handler);
    el.load();

    return () => el.removeEventListener('loadedmetadata', handler);
  }, [audioReady, voiceUrl]);

  // Start audio when countdown finishes
  useEffect(() => {
    if (preCountdown !== null || !audioReady) return;
    const voice = voiceAudioRef.current;
    const music = musicAudioRef.current;
    if (voice && voice.readyState >= 1) seekToSessionPosition(voice);
    if (music && music.readyState >= 1) seekToSessionPosition(music);
  }, [preCountdown, audioReady]);

  // Stop audio when meditation ends
  useEffect(() => {
    if (ended) {
      voiceAudioRef.current?.pause();
      musicAudioRef.current?.pause();
    }
  }, [ended]);

  // Stop audio on unmount (e.g. when the page swaps which session is rendered)
  useEffect(() => {
    return () => {
      voiceAudioRef.current?.pause();
      musicAudioRef.current?.pause();
    };
  }, []);

  // Sync volume
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (voiceAudioRef.current) voiceAudioRef.current.volume = v;
    if (musicAudioRef.current) musicAudioRef.current.volume = v;
  };

  const handleVoiceSelect = (idx: number) => {
    voiceAudioRef.current?.pause();
    setSelectedVoiceIdx(idx);
  };

  const handleClose = () => {
    voiceAudioRef.current?.pause();
    musicAudioRef.current?.pause();
    onEnded?.();
  };

  // Pre-start countdown
  useEffect(() => {
    const tick = () => {
      const secsUntilStart = Math.ceil((startTime.getTime() - Date.now()) / 1000);
      setPreCountdown(secsUntilStart > 0 ? secsUntilStart : null);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [meditation.scheduled_at]);

  // Countdown + progress + end detection
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const remaining = endTime.getTime() - now.getTime();
      if (remaining <= 0) {
        setTimeLeft('Afgelopen');
        setProgress(100);
        if (!ended) {
          setEnded(true);
          setTimeout(() => onEnded?.(), 3000);
        }
        return;
      }
      const elapsed = now.getTime() - startTime.getTime();
      setProgress(Math.min(100, (elapsed / durationMs) * 100));

      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      setTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [meditation.scheduled_at, meditation.duration_minutes, ended]);

  // Presence channel for participants
  useEffect(() => {
    if (!profile) return;

    const channel = supabase.channel(
      `samen-session-${meditation.id}`,
      { config: { presence: { key: profile.id } } },
    );

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const users: Participant[] = Object.values(state)
          .flat()
          .map((p: any) => ({
            id: p.id,
            naam: p.naam,
            avatar_url: p.avatar_url,
          }));
        setParticipants(users);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            id: profile.id,
            naam: profile.naam ?? 'Onbekend',
            avatar_url: profile.avatar_url,
          });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [meditation.id, profile?.id]);

  // Chat: fetch messages + realtime
  useEffect(() => {
    const fetchMessages = async () => {
      setChatLoading(true);
      const { data, error } = await supabase
        .from('samen_messages')
        .select('id, user_id, samen_id, content, meditation_id, created_at, profiles:user_id(naam, avatar_url)')
        .eq('samen_id', samenId)
        .eq('meditation_id', meditation.id)
        .order('created_at', { ascending: true })
        .limit(50);

      if (!error && data) {
        setMessages(
          data.map((msg: any) => ({
            ...msg,
            user_name: msg.profiles?.naam || 'Onbekend',
            avatar_url: msg.profiles?.avatar_url,
          })),
        );
      }
      setChatLoading(false);
    };

    fetchMessages();

    const channel = supabase
      .channel(`samen_session_messages_${samenId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'samen_messages',
          filter: `samen_id=eq.${samenId}`,
        },
        async (payload) => {
          const row = payload.new as any;
          if (row.meditation_id !== meditation.id) return;
          const { data: senderProfile } = await supabase
            .from('profiles')
            .select('naam, avatar_url')
            .eq('id', row.user_id)
            .single();

          const msg: Message = {
            ...row,
            user_name: senderProfile?.naam || 'Onbekend',
            avatar_url: senderProfile?.avatar_url,
          };
          setMessages((prev) =>
            prev.some((m) => m.id === msg.id) ? prev : [...prev, msg],
          );
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [samenId, meditation.id]);

  // Auto-scroll chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showChat]);

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const content = newMessage.trim();
    setNewMessage('');

    try {
      const { data, error } = await supabase
        .from('samen_messages')
        .insert({ user_id: profile.id, samen_id: samenId, content, meditation_id: meditation.id })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const msg: Message = {
          ...data,
          user_name: profile.naam ?? 'Onbekend',
          avatar_url: profile.avatar_url ?? undefined,
        };
        setMessages((prev) =>
          prev.some((m) => m.id === msg.id) ? prev : [...prev, msg],
        );
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(content);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-black text-white">
      {/* Blurred background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
          filter: 'blur(20px)',
          transform: 'scale(1.2)',
        }}
      />
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/40" />

      {/* Pre-start countdown overlay */}
      {preCountdown !== null && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm">
          <p className="text-white/60 text-lg mb-4">Meditatie begint over</p>
          <div className="text-8xl font-bold text-red-400 tabular-nums">
            {preCountdown}
          </div>
          <p className="text-white/40 text-sm mt-6">Maak je klaar...</p>
        </div>
      )}

      {/* Audio elements */}
      {voiceUrl && (
        <audio
          ref={voiceAudioRef}
          src={voiceUrl}
          loop
          preload="auto"
          playsInline
        />
      )}
      {musicUrl && (
        <audio
          ref={musicAudioRef}
          src={musicUrl}
          loop
          preload="auto"
          playsInline
        />
      )}

      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute top-8 left-8 z-50 text-white hover:text-white/80 transition-colors"
      >
        <X className="h-8 w-8" />
      </button>

      {/* Chat toggle button */}
      <button
        onClick={() => setShowChat(!showChat)}
        className="absolute top-8 right-8 z-50 text-white hover:text-white/80 transition-colors"
      >
        <MessageSquare className="h-7 w-7" />
        {messages.length > 0 && (
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] flex items-center justify-center">
            {messages.length}
          </span>
        )}
      </button>

      {/* Main content area */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        <div className="flex flex-col items-center space-y-6 w-full max-w-4xl">
          {/* Status badge */}
          <div className="flex items-center gap-2 bg-red-500/20 backdrop-blur-sm px-4 py-2 rounded-full border border-red-500/30">
            <span className="h-2 w-2 rounded-full bg-red-400 animate-pulse" />
            <span className="text-sm font-medium text-red-300 uppercase tracking-wide">
              {ended ? 'Meditatie afgelopen' : 'Meditatie is bezig'}
            </span>
          </div>

          {/* Meditation image or breathing overlay */}
          {isBreathing && exerciseData ? (
            <BreathingOverlay
              phases={exerciseData.phases}
              bpm={exerciseData.bpm}
              introDelay={exerciseData.introDelay}
              totalRounds={exerciseData.totalRounds}
              audioTime={audioTime}
              audioPlaying={!ended}
            />
          ) : (
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping [animation-duration:3s]" style={{ margin: '-16px' }} />
              <div className="absolute inset-0 rounded-lg bg-red-500/10 animate-pulse [animation-duration:4s]" style={{ margin: '-8px' }} />
              <img
                src={imageUrl || '/images/logotrans.svg'}
                alt={meditation.meditation_title}
                className="w-48 h-48 sm:w-64 sm:h-64 rounded-lg object-cover shadow-2xl relative z-10"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/images/logotrans.svg';
                }}
              />
            </div>
          )}

          {/* Title + time */}
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold">
              {meditation.meditation_title}
            </h2>
            <p className="text-white/60 mt-1">
              {startTime.toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              -{' '}
              {endTime.toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>

          {/* Voice selection buttons */}
          {voiceConfigs.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-white/60">Kies stem:</span>
              {voiceConfigs.map((voice, idx) => (
                <Button
                  key={voice.id}
                  onClick={() => handleVoiceSelect(idx)}
                  className={`${
                    selectedVoiceIdx === idx
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  } border-white/20 rounded-full px-6`}
                >
                  {voice.voice_name}
                </Button>
              ))}
            </div>
          )}

          {/* Progress bar + timer */}
          <div className="w-full max-w-[600px] space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/60 w-12 text-right">
                {(() => {
                  const elapsed = Math.max(0, Date.now() - startTime.getTime());
                  const m = Math.floor(elapsed / 60000);
                  const s = Math.floor((elapsed % 60000) / 1000);
                  return `${m}:${s.toString().padStart(2, '0')}`;
                })()}
              </span>
              <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-400 rounded-full transition-all duration-1000"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs text-white/60 w-12">{timeLeft}</span>
            </div>

            {/* Volume */}
            {!isIOS && (
            <div className="flex items-center justify-center gap-2">
              <Volume2 className="h-4 w-4 text-white/40 flex-shrink-0" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className="w-28 h-1 accent-white cursor-pointer"
              />
            </div>
            )}
          </div>

          {/* Participants */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Users className="h-4 w-4" />
              <span>
                {participants.length}{' '}
                {participants.length === 1 ? 'deelnemer' : 'deelnemers'}
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {participants.map((p) => (
                <div key={p.id} className="flex flex-col items-center gap-1">
                  <div className="relative">
                    <Avatar className="h-10 w-10 border-2 border-red-500/50">
                      <AvatarImage src={p.avatar_url ?? undefined} />
                      <AvatarFallback className="text-xs bg-white/10 text-white">
                        {p.naam?.substring(0, 2) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-400 border-2 border-black" />
                  </div>
                  <span className="text-xs text-white/60">{p.naam}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Chat panel */}
      {showChat && (
        <div className="absolute top-0 right-0 bottom-0 w-full sm:w-96 z-50 bg-black/80 backdrop-blur-md border-l border-white/10 flex flex-col">
          {/* Chat header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-red-400" />
              <span className="font-medium">Samen Chat</span>
            </div>
            <button
              onClick={() => setShowChat(false)}
              className="p-1 hover:bg-white/10 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Chat messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {chatLoading ? (
              <p className="text-white/40 text-center text-sm">
                Berichten laden...
              </p>
            ) : messages.length === 0 ? (
              <p className="text-white/40 text-center text-sm">
                Nog geen berichten.
              </p>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.user_id === profile?.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`flex ${message.user_id === profile?.id ? 'flex-row-reverse' : 'flex-row'} max-w-[85%] gap-2`}
                  >
                    <Avatar className="h-6 w-6 flex-shrink-0">
                      <AvatarImage src={message.avatar_url ?? undefined} />
                      <AvatarFallback className="text-[10px] bg-white/10 text-white">
                        {message.user_name?.substring(0, 2) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div
                        className={`px-3 py-1.5 rounded-lg text-sm ${
                          message.user_id === profile?.id
                            ? 'bg-red-600/40 text-white'
                            : 'bg-white/10 text-white'
                        }`}
                      >
                        {message.content}
                      </div>
                      <p className="text-[10px] text-white/30 mt-0.5 px-1">
                        {message.user_name} •{' '}
                        {new Date(message.created_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Chat input */}
          <form
            onSubmit={sendMessage}
            className="p-3 border-t border-white/10 flex gap-2"
          >
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Typ een bericht..."
              className="flex-grow bg-white/10 border-white/20 text-white placeholder:text-white/30"
            />
            <Button
              type="submit"
              size="icon"
              disabled={!newMessage.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </div>,
    document.body,
  );
}
